import React, { useState, useEffect } from 'react';
import { X, Upload, Loader2, CheckCircle, AlertCircle, Video, ChevronDown, ChevronRight } from 'lucide-react';
import { adminProductsService } from '@/services';
import { courseCurriculumService } from '@/services/courseCurriculum.service';
import { adminVideoService } from '@/services/adminVideoService';

interface BulkVideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
}

interface CourseOption {
  id: string;
  title: string;
  slug?: string;
}

interface TopicUploadSlot {
  moduleId: string;
  moduleName: string;
  lessonId: string;
  lessonName: string;
  segmentId: string;
  segmentName: string;
  topicId: string;
  topicNumber: number;
  topicContent: string;
  file: File | null;
  hasExistingVideo: boolean;
  uploading: boolean;
  uploadProgress: number;
  uploadComplete: boolean;
  uploadError: string | null;
}

export default function BulkVideoUploadModal({
  isOpen,
  onClose,
  onUploadComplete,
}: BulkVideoUploadModalProps) {
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [curriculum, setCurriculum] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [uploadSlots, setUploadSlots] = useState<TopicUploadSlot[]>([]);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState({ total: 0, completed: 0, failed: 0 });

  // Fetch courses on mount
  useEffect(() => {
    if (isOpen) {
      fetchCourses();
    }
  }, [isOpen]);

  // Fetch curriculum when course selected
  useEffect(() => {
    if (selectedCourse) {
      fetchCurriculum(selectedCourse);
    } else {
      setCurriculum(null);
      setUploadSlots([]);
    }
  }, [selectedCourse]);

  // Auto-expand first module
  useEffect(() => {
    if (uploadSlots.length > 0 && expandedModules.size === 0) {
      const firstModuleId = uploadSlots[0]?.moduleId;
      if (firstModuleId) {
        setExpandedModules(new Set([firstModuleId]));
      }
    }
  }, [uploadSlots]);

  const fetchCourses = async () => {
    try {
      const response = await adminProductsService.fetchCourses();
      if (response.data) {
        setCourses(
          response.data.map((course: any) => ({
            id: course.id,
            title: course.title,
            slug: course.slug || '',
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchCurriculum = async (courseId: string) => {
    setLoading(true);
    try {
      const response = await courseCurriculumService.getCourseCurriculumByCourseId(courseId);

      if (response.success && response.data) {
        setCurriculum(response.data);
        buildUploadSlots(response.data);
      }
    } catch (error) {
      console.error('Error fetching curriculum:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildUploadSlots = (curriculumData: any) => {
    const slots: TopicUploadSlot[] = [];
    const modules = curriculumData.course?.modules || [];

    modules.forEach((module: any) => {
      const lessons = module.lessons || [];

      lessons.forEach((lesson: any) => {
        const segments = lesson.segments || [];

        segments.forEach((segment: any) => {
          const topics = segment.topics || [];

          topics.forEach((topic: any) => {
            slots.push({
              moduleId: module.id,
              moduleName: `Module ${module.module_number}: ${module.title}`,
              lessonId: lesson.id,
              lessonName: `Lesson ${lesson.lesson_number}: ${lesson.title}`,
              segmentId: segment.id,
              segmentName: `Segment ${segment.segment_number}: ${segment.title}`,
              topicId: topic.id,
              topicNumber: topic.topic_number,
              topicContent: topic.content,
              file: null,
              hasExistingVideo: topic.video_exists || false,
              uploading: false,
              uploadProgress: 0,
              uploadComplete: false,
              uploadError: null,
            });
          });
        });
      });
    });

    setUploadSlots(slots);
  };

  const handleFileSelect = (topicId: string, file: File | null) => {
    setUploadSlots(prev =>
      prev.map(slot =>
        slot.topicId === topicId
          ? { ...slot, file, uploadError: null, uploadComplete: false }
          : slot
      )
    );
  };

  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const handleBulkUpload = async () => {
    const slotsToUpload = uploadSlots.filter(slot => slot.file !== null);

    if (slotsToUpload.length === 0) {
      alert('Please select at least one video to upload');
      return;
    }

    setIsUploading(true);
    setUploadStats({ total: slotsToUpload.length, completed: 0, failed: 0 });

    // Upload in parallel with concurrency limit - FIXED VERSION
    const concurrencyLimit = 2; // Reduced to 2 for stability
    let currentIndex = 0;

    const uploadSlot = async (slot: TopicUploadSlot) => {
      try {
        // Mark as uploading
        setUploadSlots(prev =>
          prev.map(s => (s.topicId === slot.topicId ? { ...s, uploading: true, uploadProgress: 0 } : s))
        );

        await adminVideoService.uploadVideoPresigned(
          {
            courseId: selectedCourse,
            segmentId: slot.segmentId,
            topicId: slot.topicId,
            file: slot.file!,
          },
          (progress) => {
            // Use requestAnimationFrame to prevent UI blocking
            requestAnimationFrame(() => {
              setUploadSlots(prev =>
                prev.map(s =>
                  s.topicId === slot.topicId ? { ...s, uploadProgress: progress.percentage } : s
                )
              );
            });
          }
        );

        // Mark as complete
        setUploadSlots(prev =>
          prev.map(s =>
            s.topicId === slot.topicId
              ? { ...s, uploading: false, uploadComplete: true, hasExistingVideo: true }
              : s
          )
        );

        setUploadStats(prev => ({ ...prev, completed: prev.completed + 1 }));
      } catch (error: any) {
        console.error(`✗ Failed to upload ${slot.topicId}:`, error);

        setUploadSlots(prev =>
          prev.map(s =>
            s.topicId === slot.topicId
              ? {
                  ...s,
                  uploading: false,
                  uploadError: error.message || 'Upload failed',
                }
              : s
          )
        );

        setUploadStats(prev => ({ ...prev, failed: prev.failed + 1 }));
      }
    };

    // Create worker function for concurrent uploads
    const uploadWorker = async () => {
      while (currentIndex < slotsToUpload.length) {
        const slotIndex = currentIndex++;
        const slot = slotsToUpload[slotIndex];
        await uploadSlot(slot);
        // Small delay between uploads to prevent overwhelming
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    };

    // Start concurrent workers
    const workers = Array(Math.min(concurrencyLimit, slotsToUpload.length))
      .fill(null)
      .map(() => uploadWorker());

    // Wait for all workers to complete
    await Promise.all(workers);

    setIsUploading(false);
    onUploadComplete();
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedCourse('');
      setCurriculum(null);
      setUploadSlots([]);
      setExpandedModules(new Set());
      setUploadStats({ total: 0, completed: 0, failed: 0 });
      onClose();
    }
  };

  if (!isOpen) return null;

  // Group slots by module
  const moduleGroups = uploadSlots.reduce((acc, slot) => {
    if (!acc[slot.moduleId]) {
      acc[slot.moduleId] = {
        moduleName: slot.moduleName,
        slots: [],
      };
    }
    acc[slot.moduleId].slots.push(slot);
    return acc;
  }, {} as Record<string, { moduleName: string; slots: TopicUploadSlot[] }>);

  const selectedSlotsCount = uploadSlots.filter(s => s.file !== null).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-lg">
              <Upload className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Bulk Video Upload</h2>
              <p className="text-sm text-gray-400 mt-1">
                Upload all videos for a course at once
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Course Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select Course
            </label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              disabled={isUploading}
              className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">-- Select a course --</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
              <span className="ml-3 text-gray-400">Loading curriculum...</span>
            </div>
          )}

          {/* Upload Slots */}
          {!loading && curriculum && (
            <>
              {/* Stats */}
              {selectedSlotsCount > 0 && (
                <div className="mb-6 p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Video className="w-5 h-5 text-indigo-400" />
                      <span className="text-indigo-300 font-medium">
                        {selectedSlotsCount} video{selectedSlotsCount !== 1 ? 's' : ''} selected for upload
                      </span>
                    </div>
                    {isUploading && (
                      <span className="text-sm text-gray-400">
                        {uploadStats.completed} / {uploadStats.total} completed
                        {uploadStats.failed > 0 && ` (${uploadStats.failed} failed)`}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Module Sections */}
              <div className="space-y-4">
                {Object.entries(moduleGroups).map(([moduleId, group]) => (
                  <div key={moduleId} className="border border-slate-600 rounded-lg overflow-hidden">
                    {/* Module Header */}
                    <button
                      onClick={() => toggleModule(moduleId)}
                      className="w-full flex items-center justify-between p-4 bg-slate-700 hover:bg-slate-600 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {expandedModules.has(moduleId) ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                        <span className="font-semibold text-white">{group.moduleName}</span>
                        <span className="text-sm text-gray-400">
                          ({group.slots.length} topic{group.slots.length !== 1 ? 's' : ''})
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">
                          {group.slots.filter(s => s.hasExistingVideo).length} have videos
                        </span>
                      </div>
                    </button>

                    {/* Module Content */}
                    {expandedModules.has(moduleId) && (
                      <div className="p-3 bg-slate-800 space-y-2">
                        {group.slots.map((slot) => (
                          <div
                            key={slot.topicId}
                            className={`p-2 rounded-lg border transition-all ${
                              slot.uploadComplete
                                ? 'bg-green-500/10 border-green-500/30'
                                : slot.uploadError
                                ? 'bg-red-500/10 border-red-500/30'
                                : slot.uploading
                                ? 'bg-blue-500/10 border-blue-500/30'
                                : 'bg-slate-700 border-slate-600'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              {/* Topic Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-indigo-400">
                                    {slot.topicId}
                                  </span>
                                  <span className="text-xs text-gray-400">•</span>
                                  <span className="text-sm text-gray-300 truncate">
                                    {slot.topicContent}
                                  </span>
                                  {slot.hasExistingVideo && !slot.uploadComplete && (
                                    <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded flex-shrink-0">
                                      Has video
                                    </span>
                                  )}
                                  {slot.uploadComplete && (
                                    <span className="flex items-center gap-1 text-xs text-green-400 flex-shrink-0">
                                      <CheckCircle className="w-3 h-3" />
                                      Uploaded
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* File Input */}
                              <div className="flex-shrink-0 w-56">
                                {slot.uploading ? (
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs text-gray-400">
                                      <span>Uploading...</span>
                                      <span>{slot.uploadProgress}%</span>
                                    </div>
                                    <div className="w-full h-1.5 bg-slate-600 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-indigo-500 transition-all duration-300"
                                        style={{ width: `${slot.uploadProgress}%` }}
                                      />
                                    </div>
                                  </div>
                                ) : slot.uploadError ? (
                                  <div className="flex items-center gap-1.5 text-xs text-red-400">
                                    <AlertCircle className="w-3.5 h-3.5" />
                                    <span className="truncate">{slot.uploadError}</span>
                                  </div>
                                ) : (
                                  <div>
                                    <input
                                      type="file"
                                      accept="video/mp4,video/webm"
                                      onChange={(e) => handleFileSelect(slot.topicId, e.target.files?.[0] || null)}
                                      disabled={isUploading}
                                      className="hidden"
                                      id={`file-${slot.topicId}`}
                                    />
                                    <label
                                      htmlFor={`file-${slot.topicId}`}
                                      className={`block px-3 py-1.5 text-xs text-center rounded-lg cursor-pointer transition-colors ${
                                        slot.file
                                          ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                                          : 'bg-slate-600 text-gray-300 hover:bg-slate-500'
                                      } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      {slot.file ? (
                                        <span className="truncate block" title={slot.file.name}>
                                          {slot.file.name}
                                        </span>
                                      ) : (
                                        'Choose video'
                                      )}
                                    </label>
                                    {slot.file && (
                                      <p className="text-xs text-gray-400 mt-0.5 text-center">
                                        {(slot.file.size / (1024 * 1024)).toFixed(1)} MB
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* No Course Selected */}
          {!loading && !curriculum && (
            <div className="text-center py-12 text-gray-400">
              <Upload className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Select a course to begin bulk upload</p>
            </div>
          )}
        </div>

        {/* Footer */}
        {curriculum && (
          <div className="flex items-center justify-between p-6 border-t border-slate-700 bg-slate-800">
            <button
              onClick={handleClose}
              disabled={isUploading}
              className="px-6 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Uploading...' : 'Cancel'}
            </button>
            <button
              onClick={handleBulkUpload}
              disabled={isUploading || selectedSlotsCount === 0}
              className="px-8 py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Uploading {uploadStats.completed}/{uploadStats.total}...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload {selectedSlotsCount} Video{selectedSlotsCount !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
